import { CheckOutlined, InfoCircleFilled } from '@ant-design/icons';
import { ConfigProvider } from 'antd';
import React, { useEffect, useState } from 'react';
import { DisplayLayout } from '../../../assets/CustomIcons/DisplayLayout';
import { SortAscending } from '../../../assets/CustomIcons/SortAscending';
import { SortDescending } from '../../../assets/CustomIcons/SortDescending';
import colorTokens from '../../../constants/design_system/colors';
import { useViewContext } from '../../../pages/Space/Context/ViewContext';
import { useGetWorkspace } from '../../../serverState/workspace';
import { WorkspaceFramework } from '../../../types/Workspace';
import {
  WorkspaceView,
  WorkspaceViewEntityType,
  WorkspaceViewGroup,
  WorkspaceViewRepresentation,
} from '../../../types/WorkspaceView';
import { Column, ViewColumn, ViewColumnUtils, WorkspaceViewColumn } from '../../../types/WorkspaceViewColumn';
import { WorkspaceViewSortDirection } from '../../../types/WorkspaceViewSortBy';
import { ConfirmSaveForEveryoneModal } from '../../Modal/ConfirmSaveForEveryoneModal';
import { ViewColumnComponentType, ViewColumnsGroup } from '../../Modal/components/ViewColumnsGroup';
import ViewRepresentationCard from '../../Modal/components/ViewRepresentationCard';
import { BaseButton } from '../../UI/BaseButton';
import { CustomSelect } from '../../UI/CustomSelect';
import BaseDropdown from '../../UI/Dropdown/BaseDropdown';

interface ViewDisplayDropdownProps {
  view: WorkspaceView;
  isViewSelectedConfig: boolean;
  onSave: (view: WorkspaceView) => void;
  onClear: () => void;
}

const ViewDisplayDropdown: React.FC<ViewDisplayDropdownProps> = ({ view, isViewSelectedConfig, onSave, onClear }) => {
  const viewContext = useViewContext();
  const workspaceQuery = useGetWorkspace();
  const framework = workspaceQuery.data?.framework || WorkspaceFramework.none;

  const [representation, setRepresentation] = useState<WorkspaceViewRepresentation>(view.representation);
  const [entityType, setEntityType] = useState<WorkspaceViewEntityType>(view.entityType);
  const [groupBy, setGroupBy] = useState<string>(Column.none);
  const [sortBy, setSortBy] = useState<string>('Manual');
  const [sortDirection, setSortDirection] = useState<WorkspaceViewSortDirection>(
    view.sortBy?.direction ?? WorkspaceViewSortDirection.desc
  );

  const [columns, setColumns] = useState<ViewColumn[]>(ViewColumnUtils.updateAndSortColumn(framework, view));

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  useEffect(() => {
    setColumns(ViewColumnUtils.updateAndSortColumn(framework, view));
    setGroupBy(view.groupBy || Column.none);
    setSortBy(view.sortBy?.column || 'Manual');
    setSortDirection(view.sortBy?.direction ?? WorkspaceViewSortDirection.desc);
    setEntityType(view.entityType);
  }, [view]);

  const updateRepresentation = (representation: WorkspaceViewRepresentation) => {
    if (representation === view.representation) {
      return;
    }
    setRepresentation(representation);

    updateCurrentView(representation, columns);
  };

  const updateColumns = (newColumns: ViewColumn[]) => {
    setColumns(newColumns);
    updateCurrentView(representation, newColumns);
  };

  const updateGroupBy = (groupBy: string) => {
    setGroupBy(groupBy);
    updateCurrentView(representation, columns, groupBy);
  };

  const updateSortBy = (sortBy: string) => {
    setSortBy(sortBy);
    updateCurrentView(representation, columns, groupBy, sortBy);
  };

  const updateSortDirection = () => {
    const direction =
      sortDirection === WorkspaceViewSortDirection.asc
        ? WorkspaceViewSortDirection.desc
        : WorkspaceViewSortDirection.asc;
    setSortDirection(direction);
    updateCurrentView(representation, columns, groupBy, sortBy, direction);
  };

  const updateColumnsSelection = (propertyId: string, isSelected: boolean) => {
    const newColumns = columns.map((property) =>
      property.id === propertyId ? { ...property, selected: isSelected } : property
    );
    setColumns(newColumns);
    updateCurrentView(representation, newColumns);
  };

  const updateCurrentView = (
    representation: WorkspaceViewRepresentation,
    columns: ViewColumn[],
    groupBy?: string,
    sortBy?: string,
    sortDirection?: WorkspaceViewSortDirection,
    entityType?: WorkspaceViewEntityType
  ) => {
    const serverColumns: WorkspaceViewColumn = {
      hidden: columns.filter((column) => !column.selected).map((column) => column.id),
      order: columns.map((column) => column.id),
    };
    const newView = {
      ...view,
      representation,
      columns: serverColumns,
      groupBy: groupBy ?? view.groupBy,
      sortBy: {
        column: sortBy ?? view.sortBy?.column,
        direction: sortDirection ?? view.sortBy?.direction ?? WorkspaceViewSortDirection.desc,
      },
      entityType: entityType ?? view.entityType,
    };

    // Apply the unsaved display change to the active view only. Do NOT write it into the
    // shared `views` array: that array is what Space re-derives the view from when you switch
    // modules and come back, so polluting it makes the unsaved config reappear (with "Reset"
    // still offered) after navigation. Unsaved edits live on the active view until the user
    // hits "Save for everyone"; navigating away discards them. (bug #2364)
    viewContext?.setView(newView);
    // Protects these edits from being overwritten when another user saves this same view.
    viewContext?.setViewDirty(true);
  };

  const handleSaveForAllClick = () => {
    setIsConfirmModalOpen(true);
  };

  const handleConfirmSave = () => {
    setIsConfirmModalOpen(false);
    onSave(view);
  };

  const handleCancelConfirm = () => {
    setIsConfirmModalOpen(false);
  };

  return (
    <>
      <ConfirmSaveForEveryoneModal
        isOpen={isConfirmModalOpen}
        onConfirm={handleConfirmSave}
        onCancel={handleCancelConfirm}
        mode="display"
      />
      <BaseDropdown
        disableTransition
        trigger={['click']}
        onOpenChange={(open) => {
          setDropdownOpen(open);
        }}
        dropdownRender={(menu) => (
          <div className="view-display-dropdown w-[256px] flex flex-col gap-2 bg-brand-white shadow px-4 py-2">
            <div className="flex flex-col gap-2">
              <div className="text-neutral-900 text-xs font-semibold">Layout</div>

              <div className="h-16 p-1 bg-neutral-300 rounded justify-center items-start inline-flex">
                <ViewRepresentationCard
                  representation={WorkspaceViewRepresentation.table}
                  setRepresentation={() => updateRepresentation(WorkspaceViewRepresentation.table)}
                  selected={representation === WorkspaceViewRepresentation.table}
                  viewGroup={view.group}
                  entityType={entityType}
                />

                <ViewRepresentationCard
                  representation={WorkspaceViewRepresentation.kanban}
                  setRepresentation={() => updateRepresentation(WorkspaceViewRepresentation.kanban)}
                  selected={representation === WorkspaceViewRepresentation.kanban}
                  viewGroup={view.group}
                  entityType={entityType}
                />
              </div>
            </div>

            {view.entityType !== WorkspaceViewEntityType.epic && (
              <div className="flex items-center justify-between">
                <div className="text-neutral-900 text-xs font-semibold">Group by:</div>
                <CustomSelect
                  options={ViewColumnUtils.groupByColumns(framework, view).map((c) => c.id)}
                  selectedOption={groupBy}
                  selectOption={updateGroupBy}
                  getTitle={(option) => ViewColumnUtils.getTitle(option as Column, framework)}
                  width="150px"
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="text-neutral-900 text-xs font-semibold">Sort by:</div>
              <div className="w-[150px] flex items-center">
                <CustomSelect
                  options={['Manual', ...ViewColumnUtils.sortByColumns(framework, view).map((c) => c.id)]}
                  selectedOption={sortBy}
                  selectOption={updateSortBy}
                  getTitle={(option) =>
                    option === 'Manual' ? 'Manual' : ViewColumnUtils.getTitle(option as Column, framework)
                  }
                  className="flex-1"
                />
                {sortBy !== 'Manual' && (
                  <div
                    className={`h-[23px] w-[23px] flex items-center justify-center rounded border border-solid border-neutral-500 text-neutral-900 hover:border-brand-taskonio-green cursor-pointer ml-1 }`}
                    onClick={updateSortDirection}
                  >
                    {sortDirection === WorkspaceViewSortDirection.asc ? <SortAscending /> : <SortDescending />}
                  </div>
                )}
              </div>
            </div>

            <div className="text-neutral-900 text-xs font-semibold leading-none mt-2">Properties</div>
            <ConfigProvider
              theme={{
                token: {
                  colorPrimary: colorTokens.blue[600],
                },
              }}
            >
              <ViewColumnsGroup
                componentType={ViewColumnComponentType.dropdown}
                columns={columns}
                updateColumns={updateColumns}
                updateColumnSelection={updateColumnsSelection}
                representation={representation}
              />
            </ConfigProvider>
            <div className="pl-2 py-2 bg-neutral-200 rounded justify-start items-center inline-flex gap-2 mt-2">
              <InfoCircleFilled className="text-neutral-800" />
              <div className="text-neutral-900 text-xs font-normal leading-none">
                Values of your hidden properties are saved.
              </div>
            </div>

            {!isViewSelectedConfig && (
              <div className="flex gap-2 justify-end">
                <BaseButton size="medium" type="text" onClick={onClear}>
                  Reset
                </BaseButton>

                <BaseButton size="medium" type="orange" onClick={handleSaveForAllClick}>
                  <CheckOutlined />
                  Save for everyone
                </BaseButton>
              </div>
            )}
          </div>
        )}
      >
        <div className={`relative ${view.group === WorkspaceViewGroup.backlog ? '' : 'mr-2'}`}>
          <BaseButton type="text" size="medium" active={isDropdownOpen} onClick={() => {}}>
            <DisplayLayout />
            Display
          </BaseButton>
          {!isViewSelectedConfig && <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-600 rounded-full" />}
        </div>
      </BaseDropdown>
    </>
  );
};

export default ViewDisplayDropdown;
